import { DeleteIcon, WarningIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Checkbox,
  Collapse,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { Container } from 'components/Container';
import { DataContainer } from 'components/DataContainer';
import { Icon } from 'components/Icon';
import { NumberCard } from 'components/NumberCard';
import { fetchApi, patchApi } from 'lib/fetchApi';
import { ArrowDown, ArrowUp, Logout } from 'lib/IconLib';
import { useApi } from 'lib/useApi';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import Comments from 'components/Comments';
import { ContainerHead } from 'components/ContainerHead';
import { StatusLabel } from 'components/StatusLabel';
import { SyncVakaPin } from 'components/SyncVakaPin';
import { colors } from 'lib/constants';
import { formatUnitNum } from 'lib/formatUnitNum';
import { Order, Payment, Prediction } from 'models/ApiModels';
export default function OrderPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const {
    data: order,
    reload: reloadOrder,
    state: orderLoadingState,
  } = useApi<Order>(`/orders/${id}`);
  const { data: pin } = useApi<string>(`/orders/${id}/pin`);
  const { data: prediction } = useApi<Prediction>('/me/orders/predict');

  const toast = useToast();

  const [isAlertOpen, setAlertIsOpen] = React.useState(false);
  const onClose = (): void => setAlertIsOpen(false);
  const cancelRef = React.useRef(null);
  const [isVitecConfirmOpen, setVitecConfirmOpen] = React.useState(false);
  const [pendingVitecValue, setPendingVitecValue] = React.useState<
    boolean | null
  >(null);
  const vitecCancelRef = React.useRef(null);

  const [userEmail, setUserEmail] = React.useState('');
  useEffect(() => {
    if (order !== undefined) {
      fetchApi(`/users/${order.userId}`).then((res) =>
        res.json().then((data) => setUserEmail(data.email)),
      );
    }
  }, [order]);

  async function handleOrderTermination(): Promise<boolean> {
    onClose();
    const terminated = await fetchApi(`/orders/${id}/terminate`);
    if (terminated.ok) {
      reloadOrder();
      toast({
        title: 'Ordern är avslutad!',
        status: 'success',
        position: 'top',
        isClosable: true,
      });
      return true;
    }
    return false;
  }

  async function handleForceTermination(): Promise<boolean> {
    onClose();
    const terminated = await fetchApi(`/orders/${id}/forceterminate`);
    if (terminated.ok) {
      reloadOrder();
      toast({
        title: 'Ordern är avslutad!',
        status: 'success',
        position: 'top',
        isClosable: true,
      });
      return true;
    }
    return false;
  }
  function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  async function updateNextPaymentAmount(
    amount: string,
    id: number,
  ): Promise<boolean> {
    const num = Number(amount);
    if (num > 0) {
      const updated = await fetchApi(`/payments/update/${id}?amount=${num}`);
      //
      if (updated.ok) {
        toast({
          title: 'Nästkommande betalning uppdaterad',
          description: `Nästa betalning är nu ${amount} kr`,
          status: 'success',
          position: 'top',
          isClosable: true,
        });
        return true;
      }
    }
    toast({
      title: 'Kunde inte sätta pris',
      description:
        'Är du säker på att det blev rätt? Bara pris högre än 0kr är tillåtet',
      status: 'error',
      position: 'top',
      isClosable: true,
    });
    return false;
  }

  async function updateVitecPayment(value: boolean): Promise<void> {
    const updated = await fetchApi(
      `/orders/${id}/vitecpayment?enabled=${value}`,
    );
    if (updated.ok) {
      reloadOrder();
      toast({
        title: 'Vitecbetalning uppdaterad',
        status: 'success',
        position: 'top',
        isClosable: true,
      });
    } else {
      toast({
        title: 'Kunde inte uppdatera vitecbetalning',
        status: 'error',
        position: 'top',
        isClosable: true,
      });
    }
  }

  async function updateCompanyField(
    field: 'orgName' | 'orgNumber',
    value: string,
  ): Promise<void> {
    const currentValue = order?.[field] ?? '';
    if (value.trim() === currentValue.trim()) {
      return;
    }

    const response = await patchApi(`/orders/${id}/company`, {
      orgName: field === 'orgName' ? value.trim() : order?.orgName,
      orgNumber: field === 'orgNumber' ? value.trim() : order?.orgNumber,
    });

    if (response.ok) {
      reloadOrder();
      toast({
        title: 'Företagsuppgiften uppdaterad',
        status: 'success',
        position: 'top',
        isClosable: true,
      });
    } else {
      toast({
        title: 'Kunde inte uppdatera företagsuppgiften',
        status: 'error',
        position: 'top',
        isClosable: true,
      });
    }
  }

  async function downloadPdf(paymentId: number): Promise<void> {
    const resp = await fetchApi(
      `/payments/generate-pdf?paymentId=${paymentId}`,
    );
    if (resp.ok) {
      toast({
        description: `PDF skapad för betalning #${paymentId}`,
        title: 'PDF',
        status: 'success',
        isClosable: true,
      });
    } else {
      toast({
        description: 'Kunde inte skapa PDF. Försök igen.',
        title: 'Fel',
        status: 'error',
        isClosable: true,
      });
      return;
    }

    const blob = await resp.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `order-${id}-payment-${paymentId}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }

  function handleVitecCheckboxChange(value: boolean): void {
    setPendingVitecValue(value);
    setVitecConfirmOpen(true);
  }

  function closeVitecConfirm(): void {
    setVitecConfirmOpen(false);
    setPendingVitecValue(null);
  }

  function confirmVitecChange(): void {
    if (pendingVitecValue === null) {
      closeVitecConfirm();
      return;
    }
    updateVitecPayment(pendingVitecValue);
    closeVitecConfirm();
  }

  if (orderLoadingState === 'loading') return <Spinner />;
  if (!order) return <p>Något gick fel"</p>;

  const capturesForDisplay = (() => {
    const hasCapturedPayments = order.payments.some(
      (payment) => payment.capturedAt !== null,
    );
    if (hasCapturedPayments || process.env.NODE_ENV === 'production') {
      return order.payments;
    }

    const fakeCapturedPayment: Payment = {
      id: -1,
      capturedAt: new Date(),
      amount: 799,
      taxAmount: 0,
      totalAmount: 799,
      dueDate: new Date(),
      isDue: false,
    };

    return [...order.payments, fakeCapturedPayment];
  })();

  return (
    <>
      <ContainerHead
        backButton={true}
        title={'Orderinfo'}
        columns={{ base: 1, sm: 2, md: 2, lg: 3, xl: 5 }}>
        <DataContainer title="Id">{order.orderReference}</DataContainer>
        <DataContainer title="Status">
          <StatusLabel status={order.status} />
        </DataContainer>
        <DataContainer title="Bokat den">
          {new Date(order.createdAt).toLocaleDateString()}
        </DataContainer>
        <DataContainer title="Tillgängligt från">
          {new Date(order.startAt ?? order.createdAt).toLocaleDateString()}
        </DataContainer>
        {order.activeTo && (
          <DataContainer title="Bokad till">
            {new Date(order.activeTo).toLocaleDateString()}
          </DataContainer>
        )}
        <DataContainer title="Anläggning">
          <Link
            to={`/anlaggningar/${order.unit.facility.id}`}
            style={{ display: 'flex' }}>
            {order.unit.facility.name}
            <Icon icon={Logout} fill="#333" />
          </Link>
        </DataContainer>
        <DataContainer title="Förrådsnummer">
          <Link
            style={{ display: 'flex' }}
            to={`/objekt/${order.unit?.id ?? ''}`}>
            {order.unit?.externalId
              ? formatUnitNum(order.unit.externalId)
              : 'Inget förråd'}
            <Icon icon={Logout} fill="#333" />
          </Link>
        </DataContainer>
        {order ? (
          <>
            <DataContainer title="Företagskund">
              {order.calculateSalesTax ? 'Ja' : 'Nej'}
            </DataContainer>
            <DataContainer title="Kundinfo">
              <Link to={`/konton/${order.userId}`} style={{ display: 'flex' }}>
                {userEmail ?? 'Inget konto'}
                <Icon icon={Logout} fill="#333" />
              </Link>
            </DataContainer>
            <DataContainer title="Månadskostnad">
              {order.monthlyAmount ?? '0'}:-
            </DataContainer>
            <DataContainer title="Flytta till Vitec">
              <Checkbox
                isChecked={order.vitecPayment}
                onChange={(e): void => {
                  handleVitecCheckboxChange(e.target.checked);
                }}
              />
              Markera som flyttad
            </DataContainer>
            {order.calculateSalesTax && (
              <>
                <DataContainer title="Företagsnamn">
                  <Editable
                    defaultValue={order.orgName ?? ''}
                    placeholder="Inget företagsnamn"
                    onSubmit={(value): void => {
                      updateCompanyField('orgName', value);
                    }}>
                    <EditablePreview _placeholder={{ color: 'gray.500' }}>
                      {order.orgName ? undefined : 'Inget företagsnamn'}
                    </EditablePreview>
                    <EditableInput />
                  </Editable>
                </DataContainer>
                <DataContainer title="Organisationsnummer">
                  <Editable
                    defaultValue={order.orgNumber ?? ''}
                    placeholder="Inget organisationsnummer"
                    onSubmit={(value): void => {
                      updateCompanyField('orgNumber', value);
                    }}>
                    <EditablePreview _placeholder={{ color: 'gray.500' }}>
                      {order.orgNumber
                        ? undefined
                        : 'Inget organisationsnummer'}
                    </EditablePreview>
                    <EditableInput />
                  </Editable>
                </DataContainer>
              </>
            )}
          </>
        ) : (
          <Spinner />
        )}
      </ContainerHead>
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}>
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Säg upp avtal
          </AlertDialogHeader>

          <AlertDialogBody>
            Är du säker på att du vill avsluta det här avtalet? Tvingar du
            uppsägning så är det ingen uppsägningstid eller vidare betalningar.
            {new Date(prediction?.lastPaymentAt ?? '').toLocaleDateString(
              'sv-SE',
            )}{' '}
            <br /> Kunden informeras om uppsägningen.
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} variant="primaryWhite" onClick={onClose}>
              Gå tillbaka
            </Button>
            <Button
              onClick={handleForceTermination}
              ml={3}
              backgroundColor={colors.system.error[900]}
              color="white">
              Tvinga uppsägning
            </Button>
            <Button variant="primary" onClick={handleOrderTermination} ml={3}>
              Säg upp
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        isOpen={isVitecConfirmOpen}
        leastDestructiveRef={vitecCancelRef}
        onClose={closeVitecConfirm}>
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Bekräfta ändring
          </AlertDialogHeader>

          <AlertDialogBody>
            {pendingVitecValue
              ? 'Obs! Måste vara tvingad uppsägning för att flytta!'
              : 'Är du säker på att du vill ta bort markeringen som flyttad?'}
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              ref={vitecCancelRef}
              variant="primaryWhite"
              onClick={closeVitecConfirm}>
              Nej
            </Button>
            <Button variant="primary" ml={3} onClick={confirmVitecChange}>
              Ja
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Container>
        <Flex direction="row" mb={8}>
          <Box mr="10" flexDir="column">
            <SyncVakaPin orderId={order.id} />
          </Box>
          <Button
            variant="primaryWhite"
            px={5}
            onClick={(): void => {
              setAlertIsOpen(true);
            }}>
            <DeleteIcon mr={3} /> Säg upp avtal
          </Button>
          {/* <IconCard
            title="Säg upp"
            icon={userIcon}
            desc="Säg upp avtal"
            onClick={(): void => {
              setAlertIsOpen(true);
            }}
          /> */}
        </Flex>
        <Flex display="flex" flexDir="column" width="100%">
          <Flex display="flex" flexDir={{ base: 'column', lg: 'row' }}>
            <Box mr="10" minW="450px" maxW="min-content">
              <Text fontWeight="700" fontFamily="Montserrat" mb={3}>
                Förrådskod
              </Text>
              <NumberCard
                backgroundColor="#F6F6F6"
                number={pin ? parseInt(pin) : 999999}
                fontSize={80}
                mb={8}
                minW="fit-content"
                maxW="min-content"
              />
            </Box>
            <Flex
              display="flex"
              align="left"
              flexDir="column"
              flexGrow={1}
              maxW="1000px"
              height="auto">
              {order.payments ? (
                <>
                  <PaymentsDrawer
                    title="Genomförda Betalningar"
                    headerTitles={['Betalningsdatum', 'Betalsätt']}>
                    <RenderCaptures
                      captures={capturesForDisplay}
                      onDownloadPdf={downloadPdf}
                    />
                  </PaymentsDrawer>

                  {order ? (
                    <PaymentsDrawer
                      title="Kommande Betalningar"
                      headerTitles={[
                        'Betalningsdatum',
                        'Betalsätt',
                        'Påminnelse skickad',
                      ]}>
                      {order.payments.map((payment: Payment) => {
                        if (payment.capturedAt) return null;
                        if (
                          order.activeTo !== null &&
                          addDays(new Date(payment.dueDate), 1) >
                            new Date(order?.activeTo ?? new Date())
                        )
                          return null;
                        return (
                          <Box
                            display="flex"
                            alignItems="center"
                            p={4}
                            key={payment.id}
                            width="100%"
                            borderBottom="1px solid rgba(175, 175, 175, .4)">
                            <Box minW="270px" mr={4}>
                              <Flex alignItems="center" gap={2}>
                                <Editable
                                  style={{
                                    color: '#797B80',
                                    fontSize: '20px',
                                    fontWeight: 500,
                                    flexWrap: 'nowrap',
                                  }}
                                  defaultValue={Math.round(
                                    payment.amount,
                                  ).toString()}
                                  onSubmit={(val: string): void => {
                                    updateNextPaymentAmount(val, payment.id);
                                  }}>
                                  <Flex
                                    alignItems="center"
                                    display="inline-flex">
                                    <EditablePreview />
                                    <EditableInput />
                                    <Text ml={1}>:-</Text>
                                  </Flex>
                                </Editable>
                                {payment.isDue && (
                                  <Tooltip
                                    label="Betalningen misslyckades!"
                                    float="left">
                                    <WarningIcon color="red.400" />
                                  </Tooltip>
                                )}
                              </Flex>
                              {payment.taxAmount > 0 && (
                                <Text fontSize="14px" fontWeight={500}>
                                  Plus moms: {Math.round(payment.taxAmount)}:-
                                </Text>
                              )}
                            </Box>
                            <Box minW="186px" mr={8} pt={1}>
                              <Text
                                color="#797B80"
                                fontSize="14px"
                                fontWeight={500}>
                                {new Date(payment.dueDate).toLocaleDateString(
                                  'sv-SE',
                                )}
                              </Text>
                            </Box>
                            <Box minW="225px" pt={1}>
                              <Text
                                color="#797B80"
                                fontSize="14px"
                                fontWeight={500}>
                                {payment.paymentMethodType}
                              </Text>
                            </Box>
                            <Box minW="225px" pt={1}>
                              <Text
                                color="#797B80"
                                fontSize="14px"
                                fontWeight={500}>
                                {payment.isDue
                                  ? new Date(
                                      order.paymentNotificationAt,
                                    ).toLocaleDateString('sv-SE')
                                  : 'Nej'}
                              </Text>
                            </Box>
                          </Box>
                        );
                      })}
                    </PaymentsDrawer>
                  ) : (
                    ''
                  )}
                </>
              ) : (
                // NO CAPTURE
                <Spinner />
              )}
            </Flex>
          </Flex>
        </Flex>
        {order && order.unit && (
          <Comments unitId={order.unit.id} orderId={order.id} />
        )}
      </Container>
    </>
  );
}

function RenderCapture({
  capture,
  downloadPdf,
}: {
  capture: Payment;
  downloadPdf?: (id: number) => void;
}): JSX.Element {
  return (
    <Box
      key={capture.id + 'PARENT'}
      display="flex"
      alignItems="center"
      p={4}
      width="100%"
      borderBottom="1px solid rgba(175, 175, 175, .4)">
      <Box minW="270px" mr={4}>
        <Text color="#797B80" fontSize="20px" fontWeight={500}>
          {Math.round(capture.amount + (capture.taxAmount ?? 0))}:-
        </Text>
        {capture.taxAmount > 0 && (
          <Text fontSize="14px" lineHeight="17px" fontWeight={500}>
            Varav moms: {Math.round(capture.taxAmount)}:-
          </Text>
        )}
      </Box>
      <Box minW="186px" mr={8} pt={1}>
        {capture.capturedAt && (
          <Text
            color="#797B80"
            fontSize="14px"
            lineHeight="17px"
            fontWeight={500}>
            {new Date(capture.capturedAt).toLocaleDateString('sv-SE')}
          </Text>
        )}
      </Box>
      <Box minW="186px" mr={8} pt={1}>
        {capture.paymentMethodType && (
          <Text
            color="#797B80"
            fontSize="14px"
            lineHeight="17px"
            fontWeight={500}>
            {capture.paymentMethodType}
          </Text>
        )}
      </Box>
      {downloadPdf && (
        <Box width="100%" pt={1}>
          <Button
            bg="#F27039"
            color="white"
            _hover={{ bg: '#2E2E2E' }}
            _active={{ bg: '#2E2E2E' }}
            onClick={() => downloadPdf(capture.id)}>
            Ladda ner kvitto
          </Button>
        </Box>
      )}
    </Box>
  );
}
function RenderCaptures({
  captures,
  onDownloadPdf,
}: {
  captures: Payment[];
  onDownloadPdf?: (paymentId: number) => void;
}): JSX.Element {
  return (
    <>
      {captures.map((capture) => {
        return (
          capture.capturedAt && (
            <RenderCapture
              key={capture.id}
              capture={capture}
              downloadPdf={onDownloadPdf}
            />
          )
        );
      })}
    </>
  );
}

function PaymentsDrawer({
  children,
  title,
  headerTitles,
}: {
  children: React.ReactNode;
  title: string;
  headerTitles?: string[];
}): JSX.Element {
  const [show, setShow] = useState(true);
  function handleToggle(): void {
    setShow(!show);
  }

  return (
    <Flex direction="column" fontFamily="Montserrat" mb={12}>
      <Box
        _hover={{ bg: '#F6F6F6', cursor: 'pointer' }}
        fontWeight="bold"
        p={3}
        width="100%"
        // height="64px"
        onClick={handleToggle}
        borderBottom="1px solid rgba(175, 175, 175, 0.4)">
        <Flex alignItems="center" justifyContent="space-between" gap={4}>
          {headerTitles?.length ? (
            <Flex alignItems="center" flex={1} minW="0">
              <Box minW="270px" mr={4}>
                <Text fontWeight={700}>{title}</Text>
              </Box>
              <Box minW="190px" mr={8}>
                <Text fontWeight={700}>{headerTitles[0]}</Text>
              </Box>
              <Box minW="190px" mr={8}>
                <Text fontWeight={700}>{headerTitles[1]}</Text>
              </Box>
              <Box minW="190px" mr={8}>
                <Text fontWeight={700}>{headerTitles[2]}</Text>
              </Box>
            </Flex>
          ) : (
            <Text fontWeight={700}>{title}</Text>
          )}
          <Box>
            {!show ? (
              <Icon fill="#333" icon={ArrowDown} />
            ) : (
              <Icon fill="#333" icon={ArrowUp} />
            )}
          </Box>
        </Flex>
      </Box>
      <Collapse unmountOnExit={true} in={show}>
        <Box width="100%" overflow="auto" maxH="500px">
          {children}
        </Box>
      </Collapse>
    </Flex>
  );
}
